import React, { createContext, useReducer, ReactNode, useCallback, useEffect, useContext } from 'react'
import {
  AccountsReceivableState,
  AccountsReceivableFilters,
  accountsReceivableInitialState,
  AccountsReceivableActionType,
  AccountsReceivableAction
} from '../interfaceTypes/accountsReceivable'
import { accountsReceivableReducer } from '../components/accounting/accountsReceivable/accountsReceivableReducer'
import { useFetchAccountsReceivableLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT } from '../constants'
import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import { formatAsEndOfDay, formatAsStartOfDay } from '../utils'

interface AccountsReceivableContextType extends AccountsReceivableState {
  dispatch: (action: AccountsReceivableAction) => void
  refetch: () => void
  setFilters: (filters: AccountsReceivableFilters) => void
}

export const AccountsReceivableContext = createContext<AccountsReceivableContextType>({
  ...accountsReceivableInitialState,
  dispatch: () => { },
  refetch: () => { },
  setFilters: () => { }
})

interface AccountsReceivableProviderProps {
  children: ReactNode
}

export const AccountsReceivableContextProvider: React.FC<AccountsReceivableProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(accountsReceivableReducer, accountsReceivableInitialState)
  const { isLoggedIn } = useContext(AuthContext)
  const { currentPage, dispatch: appDispatch } = useContext(AppContext)

  const [fetchAccountsReceivable, { loading, refetch }] = useFetchAccountsReceivableLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: () => {
      dispatch({ type: AccountsReceivableActionType.SET_AR_ENTRIES, arEntries: [] })
      dispatch({
        type: AccountsReceivableActionType.SET_AR_ENTRIES_SUMMARY,
        arEntriesSummary: {
          totalARs: 0,
          totalInvoiced: 0
        }
      })
      dispatch({ type: AccountsReceivableActionType.SET_LOADING, loading: false })
    },
    onCompleted: data => {
      if (data?.fetchAccountsReceivable?.arEntries) {
        dispatch({
          type: AccountsReceivableActionType.SET_AR_ENTRIES,
          arEntries: data.fetchAccountsReceivable.arEntries
        })
        dispatch({
          type: AccountsReceivableActionType.SET_AR_ENTRIES_SUMMARY,
          arEntriesSummary: data.fetchAccountsReceivable.summary
        })
      }

      if (data?.fetchAccountsReceivable?.pagination) {
        const { totalPages, totalCount } = data.fetchAccountsReceivable.pagination
        totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
        totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
      }

      dispatch({ type: AccountsReceivableActionType.SET_LOADING, loading: false })
    }
  })

  const setFilters = useCallback(
    (filters: AccountsReceivableFilters) => {
      dispatch({ type: AccountsReceivableActionType.SET_FILTERS, filters })
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    },
    [appDispatch]
  )

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: AccountsReceivableActionType.SET_LOADING, loading: true })

      fetchAccountsReceivable({
        variables: {
          input: {
            startDate: state.filters.startDate ? formatAsStartOfDay(state.filters.startDate) : undefined,
            endDate: state.filters.endDate ? formatAsEndOfDay(state.filters.endDate) : undefined,
            searchTerm: state.filters.searchTerm,
            page: currentPage,
            limit: PAGE_LIMIT
          }
        }
      })
    }
  }, [state.filters, currentPage, isLoggedIn, appDispatch, fetchAccountsReceivable])

  const contextValue: AccountsReceivableContextType = {
    ...state,
    loading: loading || state.loading,
    dispatch,
    refetch,
    setFilters
  }

  return <AccountsReceivableContext.Provider value={contextValue}>{children}</AccountsReceivableContext.Provider>
}
